import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  FormControl,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Code as HexTradeIcon,
  ContentCopy as CopyTradingIcon,
  FlashOn as FrontrunIcon,
  Home as HomeIcon,
  Memory as MempoolIcon,
  Rocket as RocketIcon,
  Security as ApproveIcon,
  ShowChart as GridIcon,
  SwapHoriz as TradingIcon,
  TrendingUp as CryptoMonitorIcon,
} from '@mui/icons-material';
import { AccessProvider } from './context/AccessContext';
import { GlobalProvider, useGlobal } from './context/GlobalContext';
import AccessIdentityControl from './components/AccessIdentityControl';
import GlobalStatusBar from './components/GlobalStatusBar';
import TransactionPreheatControl from './components/TransactionPreheatControl';
import { terminalTheme } from './theme/terminalTheme';

const Home = lazy(() => import('./pages/Home'));
const Trading = lazy(() => import('./pages/Trading'));
const Mempool = lazy(() => import('./pages/Mempool'));
const Frontrun = lazy(() => import('./pages/Frontrun'));
const CopyTrading = lazy(() => import('./pages/CopyTrading'));
const Approve = lazy(() => import('./pages/Approve'));
const HexTrade = lazy(() => import('./pages/HexTrade'));
const MarketCap = lazy(() => import('./pages/MarketCap'));
const Launchpad = lazy(() => import('./pages/Launchpad'));
const FourmemeLegacy = lazy(() => import('./pages/Fourmeme'));
const CryptoMonitorPage = lazy(() => import('./pages/CryptoMonitor'));
const Admin = lazy(() => import('./pages/Admin'));

const NAV_ITEMS = [
  { label: '首頁', value: '/', icon: <HomeIcon /> },
  { label: 'Trading', value: '/trading', icon: <TradingIcon /> },
  { label: 'Mempool', value: '/mempool', icon: <MempoolIcon /> },
  { label: 'Frontrun', value: '/frontrun', icon: <FrontrunIcon /> },
  { label: 'Copy Trading', value: '/copy-trading', icon: <CopyTradingIcon /> },
  { label: 'Approve', value: '/approve', icon: <ApproveIcon /> },
  { label: 'Hex Trade', value: '/hextrade', icon: <HexTradeIcon /> },
  { label: '市值', value: '/marketcap', icon: <GridIcon /> },
  { label: 'Launchpad', value: '/fourmeme', icon: <RocketIcon /> },
  { label: 'Crypto Monitor', value: '/crypto-monitor', icon: <CryptoMonitorIcon /> },
];

function NavigationTabs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const value = NAV_ITEMS.some((item) => item.value === pathname) ? pathname : false;
  return (
    <Box
      data-testid="terminal-navigation"
      sx={{ width: '100%' }}
    >
      <FormControl size="small" fullWidth sx={{ display: { xs: 'flex', sm: 'none' }, px: 1, py: 0.75 }}>
        <Select
          value={value || '/'}
          onChange={(event) => navigate(event.target.value)}
          aria-label="主功能導航"
        >
          {NAV_ITEMS.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tabs
        value={value}
        textColor="inherit"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          display: { xs: 'none', sm: 'flex' },
          minHeight: 42,
          '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': {
            minHeight: 42,
            color: 'text.secondary',
            '&.Mui-selected': { color: 'primary.light' },
          },
        }}
      >
        {NAV_ITEMS.map((item) => (
          <Tab
            key={item.value}
            label={item.label}
            value={item.value}
            icon={item.icon}
            iconPosition="start"
            component={Link}
            to={item.value}
          />
        ))}
      </Tabs>
    </Box>
  );
}

const RouteFallback = () => <Box sx={{ py: 4 }}><LinearProgress /></Box>;

function ApplicationRoutes() {
  const { selectedChain } = useGlobal();
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/trading" element={<Trading key={`trading:${selectedChain}`} />} />
      <Route path="/mempool" element={<Mempool key={`mempool:${selectedChain}`} />} />
      <Route path="/frontrun" element={<Frontrun key={`frontrun:${selectedChain}`} />} />
      <Route path="/copy-trading" element={<CopyTrading key={`copy-trading:${selectedChain}`} />} />
      <Route path="/approve" element={<Approve key={`approve:${selectedChain}`} />} />
      <Route path="/hextrade" element={<HexTrade key={`hex-trade:${selectedChain}`} />} />
      <Route path="/marketcap" element={<MarketCap key={`market-cap:${selectedChain}`} />} />
      <Route path="/fourmeme" element={<Launchpad key={`launchpad:${selectedChain}`} />} />
      <Route path="/fourmeme-legacy" element={<FourmemeLegacy key={`fourmeme-legacy:${selectedChain}`} />} />
      <Route path="/crypto-monitor" element={<CryptoMonitorPage />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={terminalTheme}>
      <CssBaseline />
      <Router>
        <AccessProvider>
          <GlobalProvider>
            <Box
              data-testid="terminal-shell"
              data-density="professional-terminal"
              sx={{ minHeight: '100vh', bgcolor: 'background.default' }}
            >
              <AppBar
                data-testid="terminal-appbar"
                position="sticky"
                elevation={0}
                sx={{
                  bgcolor: 'rgba(12,17,22,0.96)',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backdropFilter: 'blur(16px)',
                }}
              >
                <Toolbar sx={{ minHeight: { xs: 56, sm: 60 }, gap: 2, justifyContent: 'space-between' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography component="h1" variant="h5" sx={{ color: 'text.primary', fontSize: { xs: '1.15rem', sm: '1.45rem' } }}>
                      MaikoSniper
                    </Typography>
                    <Typography variant="caption" color="text.secondary">專業鏈上執行終端</Typography>
                  </Box>
                  <AccessIdentityControl />
                </Toolbar>
                <Box sx={{ px: { xs: 0, sm: 2 }, borderTop: '1px solid', borderColor: 'divider' }}>
                  <NavigationTabs />
                </Box>
              </AppBar>

              <GlobalStatusBar />
              <TransactionPreheatControl />
              <Container data-testid="terminal-content" maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
                <Suspense fallback={<RouteFallback />}>
                  <ApplicationRoutes />
                </Suspense>
              </Container>
            </Box>
          </GlobalProvider>
        </AccessProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
